import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../AppIcon';
import { useTheme } from './ThemeProvider';

const ThemeSwitcher = ({ 
  variant = 'default', 
  size = 'md',
  showLabel = false,
  className = '',
  position = 'relative'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { theme, setTheme, themes, getCurrentTheme, getNextTheme } = useTheme();
  const dropdownRef = useRef(null);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(event?.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const currentTheme = getCurrentTheme();
  const nextTheme = getNextTheme();

  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-10 h-10',
    lg: 'w-12 h-12'
  };

  const iconSizes = {
    sm: 16,
    md: 20,
    lg: 24
  };

  // Simple toggle variant
  if (variant === 'toggle') {
    return (
      <motion.button
        onClick={() => setTheme(nextTheme?.id)}
        className={`${sizeClasses?.[size]} bg-card hover:bg-muted border border-border rounded-lg flex items-center justify-center transition-colors duration-200 group ${className}`}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        title={`Switch to ${nextTheme?.name} theme`}
      >
        <Icon 
          name={currentTheme?.icon} 
          size={iconSizes?.[size]} 
          className="text-foreground group-hover:text-accent transition-colors duration-200" 
        />
        {showLabel && (
          <span className="ml-2 text-sm font-medium text-foreground">
            {currentTheme?.name}
          </span>
        )}
      </motion.button>
    );
  }

  // Dropdown variant
  return (
    <div className={`${position} ${className}`} ref={dropdownRef}>
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className={`${sizeClasses?.[size]} bg-card hover:bg-muted border border-border rounded-lg flex items-center justify-center transition-colors duration-200 group ${showLabel ? 'px-3' : ''}`}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        title={`Current theme: ${currentTheme?.name}`}
      >
        <Icon 
          name={currentTheme?.icon} 
          size={iconSizes?.[size]} 
          className="text-foreground group-hover:text-accent transition-colors duration-200" 
        />
        {showLabel && (
          <>
            <span className="ml-2 text-sm font-medium text-foreground">
              {currentTheme?.name}
            </span>
            <Icon 
              name={isOpen ? 'ChevronUp' : 'ChevronDown'} 
              size={14} 
              className="ml-1 text-text-secondary transition-transform duration-200" 
            />
          </>
        )}
      </motion.button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute top-full mt-2 left-0 bg-card border border-border rounded-lg shadow-lg z-50 min-w-[200px] overflow-hidden"
            style={{
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)'
            }}
          >
            <div className="p-1">
              {themes?.map((themeOption, index) => (
                <motion.button
                  key={themeOption?.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.15, delay: index * 0.05 }}
                  onClick={() => {
                    setTheme(themeOption?.id);
                    setIsOpen(false);
                  }}
                  className={`w-full flex items-center space-x-3 px-3 py-2.5 text-left text-sm rounded-md transition-colors duration-150 ${
                    theme === themeOption?.id 
                      ? 'bg-accent text-accent-foreground' 
                      : 'text-foreground hover:bg-muted/50'
                  }`}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                    theme === themeOption?.id 
                      ? 'bg-accent-foreground/20' 
                      : 'bg-muted/30'
                  }`}>
                    <Icon 
                      name={themeOption?.icon} 
                      size={16} 
                      className={
                        theme === themeOption?.id 
                          ? 'text-accent-foreground' 
                          : 'text-text-secondary'
                      } 
                    />
                  </div>
                  <div className="flex-1">
                    <div className="font-medium">{themeOption?.name}</div>
                    <div className={`text-xs ${
                      theme === themeOption?.id 
                        ? 'text-accent-foreground/70' 
                        : 'text-text-secondary'
                    }`}>
                      {themeOption?.description}
                    </div>
                  </div>
                  {theme === themeOption?.id && (
                    <Icon 
                      name="Check" 
                      size={14} 
                      className="text-accent-foreground" 
                    />
                  )}
                </motion.button>
              ))}
            </div>
            
            {/* Divider */}
            <div className="border-t border-border mx-1" />
            
            {/* System preference option */}
            <div className="p-1">
              <button
                onClick={() => {
                  localStorage.removeItem('theme');
                  const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)')?.matches;
                  setTheme(systemPrefersDark ? 'dark' : 'light');
                  setIsOpen(false);
                }}
                className="w-full flex items-center space-x-3 px-3 py-2 text-left text-sm rounded-md text-text-secondary hover:bg-muted/30 hover:text-foreground transition-colors duration-150"
              >
                <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-muted/30">
                  <Icon name="Monitor" size={16} className="text-text-secondary" />
                </div>
                <div className="flex-1">
                  <div className="font-medium">System</div>
                  <div className="text-xs text-text-secondary">
                    Follow system preference
                  </div>
                </div>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ThemeSwitcher;